import {
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SignIcon from "assets/icons1/SignatureFieldIconRed.svg";
import InitialsIcon from "assets/icons1/InitialsFieldIconRed.svg";
import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import { useAppSelector } from "redux/hooks";
import { selectScreen } from "redux/appSlice";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { useState, useEffect } from "react";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";
import {
  emitFieldCommit,
  getInitialsImage,
  getSignatureImage,
} from "../ParticipantScreenService";
import SignaturePad from "../SignaturePad/SignaturePad";
import {
  uploadInitialsImage,
  uploadSignImage,
} from "screens/SignRequest/SignRequestService";
import { ParticipantScreenStyles } from "../ParticipantScreenStyle";
import { useAppDispatch } from "redux/hooks";
import { setLoading } from "redux/appSlice";
import SavedSignatureDialog from "../components/SavedSignatureDialog";

const SignatureFields = (props: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  currentReviewer: RequestParticipant;
  signImage?: string;
  initialsImage?: string;
  /** The signer has already put this image on another field of the same type. */
  savedImageAlreadyPlaced?: boolean;
  frameX: number;
  frameY: number;
  isInitialsPad?: boolean;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  coords?: any;
  setLoading?: Function;
  disableFields?: boolean;
}) => {
  const theme = useTheme();
  const classes = ParticipantScreenStyles(theme);
  const { coords } = props;
  const urlParams = window.location.search;
  const reqToken = new URLSearchParams(urlParams).get("id");
  const isMobile = useAppSelector(selectScreen);
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [field, setField] = useState<any>(props.field);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [signatureExist, setSignatureExist] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setField(props.field);
  }, [props]);

  const handleImageUpload = async (
    bs64string: string,
    isInitialsImg: boolean,
  ) => {
    try {
      dispatch(setLoading(true));

      const base64String: string = bs64string;
      const fileName: string = `${props.field.name}.png`;

      const file = await base64ToImageFile(base64String, fileName);
      let newformData = new FormData();
      newformData.append("file", file!, file!.name);

      isInitialsImg
        ? await uploadInitialsImage(reqToken, newformData)
        : await uploadSignImage(reqToken, newformData);
      dispatch(setLoading(false));
      props.signingJourneyOn && props.moveToFieldLocation(props.field.id + 1);
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
    }
  };

  const base64ToImageFile = (
    base64String: string,
    fileName: string,
  ): Promise<File | null> => {
    const str = base64String.split(",")[0];
    const match = str.match(/^data:(.*?);base64$/);
    const contentType = match ? match[1] : "";
    const byteCharacters = atob(base64String.split(",")[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = 270;
        canvas.height = 130;
        context?.drawImage(img, 0, 0, 270, 130);
        canvas.toBlob((croppedBlob) => {
          if (croppedBlob) {
            const file = new File([croppedBlob], fileName, {
              type: contentType,
            });
            resolve(file);
          } else {
            resolve(null);
          }
        });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  const handleDelete = () => {
    // Don't allow delete when fields are disabled
    if (!props.disableFields) return;

    const hadValue = isTruthy(props.field.value);
    props.dispatch({
      type: "UPDATE_FIELD",
      fileId: props.selectedFile.unsignedPdf || props.selectedFile.unsignedDoc,
      fieldId: props.field.name,
      value: "",
    });
    if (hadValue) emitFieldCommit();
  };

  /** Stamp the already-fetched saved image onto this field. */
  const applySavedImage = () => {
    const savedImage = props.isInitialsPad
      ? props.initialsImage
      : props.signImage;

    setField({ ...props.field, value: savedImage });
    props.dispatch({
      type: "UPDATE_FIELD",
      fileId: props.selectedFile.unsignedPdf || props.selectedFile.unsignedDoc,
      fieldId: props.field.name,
      value: savedImage,
    });
    emitFieldCommit();
    props.signingJourneyOn && props.moveToFieldLocation(props.field.id + 1);
  };

  const handleSignFieldClick = async () => {
    // Don't allow signing when fields are disabled
    if (!props.disableFields) return;

    // If field already has a value, don't overwrite it - just open the signature pad if needed
    if (isTruthy(props.field.value)) {
      // Field already has a signature, open pad to edit if needed
      setIsOpen(true);
      return;
    }

    const imgAvailable = props.isInitialsPad
      ? isTruthy(props.initialsImage)
      : isTruthy(props.signImage);
    if (imgAvailable) {
      // Once the signer has adopted the image on one field the rest fill
      // straight away. Before that the image is only cached from an earlier
      // click, so every click still has to be confirmed in the dialog.
      props.savedImageAlreadyPlaced
        ? applySavedImage()
        : setSignatureExist(true);
    } else {
      try {
        dispatch(setLoading(true));
        props.isInitialsPad
          ? await getInitialsImage(
              reqToken!,
              setSignatureExist,
              props.dispatch,
              setIsOpen,
            )
          : await getSignatureImage(
              reqToken!,
              setSignatureExist,
              props.dispatch,
              setIsOpen,
            );
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  /** Drop the stored image and switch to the pad's Type / Draw / Upload tabs. */
  const handleEditSavedImage = () => {
    if (!props.disableFields) return;

    props.isInitialsPad
      ? props.dispatch({ type: "UPDATE_INITIALS_IMAGE", initialsImage: "" })
      : props.dispatch({ type: "UPDATE_SIGN_IMAGE", signImage: "" });
    setSignatureExist(false);
    setIsOpen(true);
  };

  return (
    <>
      <SavedSignatureDialog
        open={signatureExist}
        image={props.isInitialsPad ? props.initialsImage : props.signImage}
        isInitialsPad={props.isInitialsPad}
        enabled={!!props.disableFields}
        onClose={() => setSignatureExist(false)}
        onAccept={() => {
          if (!props.disableFields) return;
          setSignatureExist(false);
          applySavedImage();
        }}
        onEdit={handleEditSavedImage}
      />
      {/* {props.signStatus === strings.pending ? ( */}
      {isTruthy(props.currentReviewer.email) ? (
        <button
          style={{
            width: isTruthy(coords) ? coords.pageWidth : field.w,
            height: isTruthy(coords) ? coords.pageHeight : field.h,
            border: `1.5px dashed ${fieldColorFor(field.signerIndex)}`,
            borderRadius: "6px",
            backgroundColor: `${fieldColorFor(field.signerIndex)}14`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2px 6px",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              ...classes.signHere,
              color: fieldColorFor(field.signerIndex),
              fontWeight: 600,
              lineHeight: 1.15,
              textAlign: "center",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: {
                xs: "0.45rem",
                sm: "0.7rem",
                md: "0.8rem",
                lg: "0.85rem",
              },
            }}
          >
            {props.isInitialsPad ? "Initials Field" : "Sign Field"}
          </Typography>
        </button>
      ) : (
        <>
          {isTruthy(field.value) ? (
            <>
              <button
                id={`signature-field-button-${field.name}`}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={`${
                  props.isInitialsPad ? "Initials" : "Signature"
                } options`}
                onContextMenu={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                }}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  if (props.disableFields) setAnchorEl(e.currentTarget);
                }}
                onContextMenuCapture={(e) => {
                  e.preventDefault();
                  if (props.disableFields) setAnchorEl(e.currentTarget);
                }}
                style={{
                  // position: "absolute",
                  border: !props.disableFields
                    ? `1px dashed ${fieldColorFor(field.signerIndex)}`
                    : "1px dashed red",
                  borderRadius: "5px",
                  background: !props.disableFields
                    ? `${fieldColorFor(field.signerIndex)}17`
                    : "transparent",
                  padding: 0,
                  width: isTruthy(coords) ? coords.pageWidth : field.w,
                  height: isTruthy(coords) ? coords.pageHeight : field.h,
                  cursor: !props.disableFields ? "not-allowed" : "pointer",
                }}
              >
                <img
                  src={field.value}
                  style={{
                    width: isTruthy(coords) ? coords.pageWidth : field.w,
                    height: isTruthy(coords) ? coords.pageHeight : field.h,
                    opacity: !props.disableFields ? 0.6 : 1,
                  }}
                  alt={
                    props.isInitialsPad
                      ? "User initials signature"
                      : "User signature"
                  }
                />
              </button>
              <Menu
                id={`signature-field-menu-${field.name}`}
                anchorEl={anchorEl}
                anchorOrigin={{ horizontal: "center", vertical: "center" }}
                transformOrigin={{ horizontal: "center", vertical: "top" }}
                open={open}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                  "aria-labelledby": `signature-field-button-${field.name}`,
                }}
              >
                <MenuItem
                  onClick={() => {
                    if (!props.disableFields) return;
                    setIsOpen(true);
                    setAnchorEl(null);
                  }}
                  disabled={!props.disableFields}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!props.disableFields) return;
                    handleDelete();
                    setAnchorEl(null);
                  }}
                  disabled={!props.disableFields}
                >
                  Delete
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Tooltip title={props.field.toolTip} placement="top" arrow>
              <button
                onClick={() => {
                  props.disableFields && handleSignFieldClick();
                }}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  width: isTruthy(coords) ? coords.pageWidth : field.w,
                  height: isTruthy(coords) ? coords.pageHeight : field.h,
                  border: !props.disableFields
                    ? `1px dashed ${fieldColorFor(field.signerIndex)}`
                    : props.currentFieldOnFocus === field.id
                      ? "1.5px solid red"
                      : "1.5px dashed red",
                  borderRadius: "6px",
                  backgroundColor: !props.disableFields
                    ? field.required
                      ? `${fieldColorFor(field.signerIndex)}17`
                      : "transparent"
                    : field.required
                      ? "rgb(252, 224, 224)"
                      : "transparent",
                  boxShadow:
                    props.disableFields &&
                    props.currentFieldOnFocus === field.id
                      ? "0 0 0 3px rgba(255, 0, 0, 0.10)"
                      : "none",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "2px 6px",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  cursor: !props.disableFields ? "not-allowed" : "pointer",
                  opacity: 1,
                  transition: "box-shadow 0.15s ease, border-color 0.15s ease",
                }}
              >
                <Stack
                  direction={"row"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  spacing={0.5}
                  sx={{ maxWidth: "100%", minWidth: 0 }}
                >
                  {!isMobile &&
                    props.currentFieldOnFocus === field.id &&
                    props.disableFields && (
                      <img
                        src={props.isInitialsPad ? InitialsIcon : SignIcon}
                        alt="signature"
                        style={{
                          width: "0.9rem",
                          height: "0.9rem",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  <Typography
                    fontSize={isMobile ? 6 : "0.8rem"}
                    sx={{
                      color: !props.disableFields
                        ? `${fieldColorFor(field.signerIndex)}`
                        : "signer.danger.main", // "red" was 3.21:1 on the field fill
                      fontWeight: 600,
                      lineHeight: 1.15,
                      textAlign: "center",
                      minWidth: 0,
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: {
                        xs: "0.45rem",
                        sm: "0.7rem",
                        md: "0.8rem",
                        lg: "0.9rem",
                        xl: "0.9rem",
                      },
                    }}
                  >
                    {!props.disableFields
                      ? props.isInitialsPad
                        ? "Initials"
                        : "Signature"
                      : props.isInitialsPad
                        ? "Add Initials"
                        : "Sign here"}
                  </Typography>
                </Stack>
              </button>
            </Tooltip>
          )}
        </>
      )}

      {props.disableFields && isOpen && (
        <SignaturePad
          dispatch={props.dispatch}
          selectedFile={props.selectedFile}
          field={field}
          setField={setField}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          handleImageUpload={handleImageUpload}
          width={isMobile ? 310 : 500}
          height={isMobile ? 390 : 270}
          isInitialsPad={props.isInitialsPad}
          currentFieldOnFocus={props.currentFieldOnFocus}
          moveToFieldLocation={props.moveToFieldLocation}
          replacesSignedFields={!!props.savedImageAlreadyPlaced}
        />
      )}
    </>
  );
};

export default SignatureFields;
